import {
  Avatar,
  Box,
  Divider,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useRef } from "react";
import { useOpenTaskAction } from "features/Resources/Task/Components/Action/Button";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import CDS from "design-system";
import { Task } from "features/Resources/Task/models";
import {
  setNewTaskInQueryClient,
  useMarkCompleteTask,
  useSkipTask,
  useUpdateTask,
} from "features/Resources/Task/hooks";
import { TASK_STATUSES } from "features/Resources/Task/constants";
import { parseDateOnly } from "helpers/time";
import { TASK_NON_EDITABLE_STATUSES } from "features/Resources/Shared/constants";
import { removeLocalData } from "features/Resources/Shared/helpers";
import { useQueryClient } from "@tanstack/react-query";
import { apiTaskById } from "helpers/url/api/v2";
import useAutoSave from "components/FormElements/useAutoSave";
import TagByResourceType from "components/Tag/TagByResourceType";
import NameField from "features/Resources/Shared/Components/Fields/NameField";
import {
  ListIssuesByAction,
  ListIssuesByActionDuringPending,
} from "features/Issues/Components/ListIssues/ListIssuesByAction";
import { FloatingField } from "design-system";
import { ShowRichText } from "components/FormElements/Fields/RichTextAreaField";
import useCaminoStore from "stores/caminoStore";
import ROLE from "models/role";
import { convertToLocalDateString, dateToSimpleDateString } from "helpers/time";
import TaskThreeDotOptionMenu from "../Components/Button/TaskThreeDotOptionMenu";
import TaskStartField from "../Components/Fields/TaskStartField";
import TaskActionButtons from "../Components/TaskActionButtons";
import AssigneeSelect from "../Components/AssigneeSelect";
import SendReminderModal from "features/SendMessage/Components/SendReminderModal";
import NewHireStackedLabel from "features/NewHireJourney/Components/NewHireStackedLabel";

const ALLOWED_STATUSES = (["active", "in_progress", "blocked", "completed", "skipped"] as Task["status"][]).map(
  (value) => ({ value, ...TASK_STATUSES.get(value)! }),
);


function updateStartOn(data: Partial<Task>) {
  const newData = removeLocalData(data);
  if (newData?.startedAt) {
    newData.startOn = dateToSimpleDateString(newData.startedAt);
    delete newData.startedAt;
  }
  return newData;
}

function startedAtAsString(startedAt: Date | undefined, timezone: string) {
  if (!startedAt || !timezone) return undefined;
  return convertToLocalDateString(startedAt, timezone);
}

interface TaskFormProps {
  data: Task;
  includeHeader: boolean | undefined;
  includeMenu: boolean | undefined;
  includeReminders: boolean | undefined;
  isPending: boolean;
  timezone: string;
}

export default function TaskForm({ data, includeHeader, includeMenu, isPending, timezone }: TaskFormProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useCaminoStore((state) => state.currentUser);
  const canEditDescription = user?.permission?.includes(ROLE.PEOPLE_TEAM)
    || user?.permission?.includes(ROLE.COMPANY_ADMIN)
    || data.createdById === user?.id;
  const reminderDisclosure = useDisclosure();
  const dueDateInputRef = useRef<HTMLInputElement>(null);
  const { startedAt, ...rest } = data;
  const form = useForm<Task>({
    values: {
      ...rest,
      startedAt: startedAtAsString(startedAt, timezone),
    },
  });
  const { control } = form;

  const isDisabled = TASK_NON_EDITABLE_STATUSES.includes(data.status || "");
  const isUpcoming = data.status === "upcoming";

  const { mutate: updateTask } = useUpdateTask(data?.id);
  const markComplete = useMarkCompleteTask(data);
  const skipTask = useSkipTask(data);

  const openTaskAction = useOpenTaskAction();

  const handleActionClick = useCallback(() => {
    if (!data) return;
    openTaskAction(data.id, data.taskAction);
  }, [data, openTaskAction]);

  const handleDuePillClick = useCallback(() => {
    const input = dueDateInputRef.current;
    if (!input) return;
    if (typeof (input as HTMLInputElement & { showPicker?: () => void }).showPicker === "function") {
      (input as HTMLInputElement & { showPicker: () => void }).showPicker();
    } else {
      input.click();
    }
  }, []);

  const transformData = (updateData: Partial<Task>) => ({ task: updateStartOn(updateData) });

  useAutoSave({
    form,
    transformData,
    submitUrl: apiTaskById(data?.id),
    onSuccess: (res: any) => {
      setNewTaskInQueryClient(queryClient, res);
      toast({ title: "Task updated.", status: "success" });
    },
  });

  const startedAtDisplay = data.startedAt
    ? format(new Date(convertToLocalDateString(data.startedAt, timezone)), "MMM d, yyyy")
    : null;

  return (
    <VStack spacing={1} align="stretch" width="100%">
      {includeHeader && (
        <HStack justify="space-between" align="center" mb={2}>
          <HStack spacing={2} align="center" flex={1} minW={0}>
            <TagByResourceType type="task" />
            <NameField form={form} disabled={isDisabled} />
          </HStack>
          <HStack spacing={2} flexShrink={0}>
            <TaskActionButtons
              task={data}
              onMarkComplete={markComplete}
              onSkip={skipTask}
              onActionClick={handleActionClick}
            />
            {includeMenu && (
              <TaskThreeDotOptionMenu task={data} exclude={["view", "mark_complete", "skip"]} onOpenRemind={reminderDisclosure.onOpen} />
            )}
          </HStack>
        </HStack>
      )}

      {data && (
        <SendReminderModal
          isOpen={reminderDisclosure.isOpen}
          onClose={reminderDisclosure.onClose}
          tasks={[data]}
          preselectedUser={data.assignee}
        />
      )}

      {/* Pill row */}
      <Flex gap={2} flexWrap="wrap" align="center" mb={2}>
        {/* Status */}
        <Menu>
          <MenuButton
            as={CDS.BasePill}
            opacity={isUpcoming ? 0.5 : 1}
            cursor={isUpcoming ? "not-allowed" : "pointer"}
            _hover={isUpcoming ? {} : undefined}
            pointerEvents={isUpcoming ? "none" : "auto"}
          >
            <Flex direction="column" gap="0">
              <Text fontSize="10px" color="content.tertiary" fontWeight="500" lineHeight="1.3">
                Status
              </Text>
              <HStack spacing="1" align="center">
                <Box
                  w="6px"
                  h="6px"
                  borderRadius="full"
                  bg={TASK_STATUSES.get(data.status)?.colors ?? "content.primary"}
                  flexShrink={0}
                />
                <Text fontSize="12px" fontWeight="700" color="content.primary" lineHeight="1.3">
                  {TASK_STATUSES.get(data.status)?.label ?? ""}
                </Text>
              </HStack>
            </Flex>
          </MenuButton>
          <MenuList fontSize="sm" minW="140px">
            {ALLOWED_STATUSES.map((s) => (
              <MenuItem
                key={s.value}
                fontWeight={data.status === s.value ? "700" : "normal"}
                onClick={() => updateTask({ status: s.value })}
                icon={
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg={s.colors ?? "content.primary"}
                    flexShrink={0}
                  />
                }
              >
                {s.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        {/* Assignee */}
        <Popover placement="bottom-start" isLazy>
          {({ onClose }) => (
            <>
              <PopoverTrigger>
                <CDS.BasePill alignItems="center" gap={2}>
                  <Avatar
                    name={data.assignee?.fullName}
                    src={data.assignee?.avatarUrl ?? undefined}
                    size="xs"
                    bg="cdsIndigo.300"
                    color="white"
                    flexShrink={0}
                  />
                  <Flex direction="column" gap="0">
                    <Text fontSize="10px" color="content.tertiary" fontWeight="500" lineHeight="1.3">
                      Assignee
                    </Text>
                    <Text fontSize="12px" fontWeight="700" color="content.primary" lineHeight="1.3">
                      {data.assignee?.fullName ?? "Unassigned"}
                    </Text>
                  </Flex>
                </CDS.BasePill>
              </PopoverTrigger>
              <PopoverContent w="260px" overflow="visible">
                <PopoverBody p={2} overflow="visible">
                  <AssigneeSelect
                    assignee={data.assignee}
                    onSelect={(id) => {
                      updateTask({ assigneeId: id });
                      onClose();
                    }}
                  />
                </PopoverBody>
              </PopoverContent>
            </>
          )}
        </Popover>

        {/* Starts */}
        <TaskStartField
          task={data}
          startedAtDisplay={startedAtDisplay}
          onChangeStartOn={(date) => updateTask({ startOn: date })}
        />

        {/* Due */}
        <CDS.BasePill onClick={handleDuePillClick} position="relative">
          <Flex direction="column" gap="0">
            <Text fontSize="10px" color="content.tertiary" fontWeight="500" lineHeight="1.3">
              Due
            </Text>
            <Box
              display="inline-block"
              bg={data.isOverdue ? "statusRed.bg" : "transparent"}
              borderRadius="4px"
              px={data.isOverdue ? "4px" : 0}
            >
              <Text fontSize="12px" fontWeight="700" color="content.primary" lineHeight="1.3">
                {data.dueOn ? format(parseDateOnly(data.dueOn), "MMM d, yyyy") : "—"}
              </Text>
            </Box>
          </Flex>
          <input
            ref={dueDateInputRef}
            type="date"
            style={{ position: "absolute", opacity: 0, width: 0, height: 0, top: 0, left: 0, pointerEvents: "none" }}
            value={data.dueOn ? format(parseDateOnly(data.dueOn), "yyyy-MM-dd") : ""}
            onChange={(e) => {
              if (e.target.value) {
                const [year, month, day] = e.target.value.split("-").map(Number);
                updateTask({ dueOn: new Date(year, month - 1, day) });
              }
            }}
          />
        </CDS.BasePill>

        {/* Onboarding Journey — last in the row */}
        {data.newHireJourneyId && (
          <NewHireStackedLabel journeyId={data.newHireJourneyId} emphasized />
        )}
      </Flex>

      {isPending ? (
        <ListIssuesByActionDuringPending prefixId={data?.prefixId} align="flex-start" />
      ) : (
        <ListIssuesByAction prefixId={data?.prefixId} align="flex-start" />
      )}

      <Divider my="4" />
      {canEditDescription ? (
        <FloatingField name="description">
          <FloatingField.Label>Description</FloatingField.Label>
          <FloatingField.Trix control={control} name="description" hideToolbar />
        </FloatingField>
      ) : (
        (data.descriptionDisplay || data.description)
          ? <ShowRichText label="Description" richText={data.descriptionDisplay ?? data.description ?? ""} />
          : null
      )}
    </VStack>
  );
}
